import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Download, ExternalLink, FileText, X, AlertTriangle, Loader2 } from 'lucide-react';
import api from '../lib/api';
import OnlyOfficeEditorModal from './OnlyOfficeEditorModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Office file extensions that can be previewed via OnlyOffice
const OFFICE_EXTENSIONS = [
    '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.odt', '.ods', '.odp', '.csv',
];

// Extensions that browsers can display inline in a new tab
const BROWSER_VIEWABLE_EXTENSIONS = [
    '.pdf', '.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp',
    '.mp4', '.webm', '.ogg', '.mov',
    '.mp3', '.wav', '.ogg', '.flac', '.aac',
    '.txt', '.csv', '.json', '.xml', '.md', '.log', '.html', '.css', '.js',
];

function getExtension(filename) {
    if (!filename) return '';
    const idx = filename.lastIndexOf('.');
    return idx >= 0 ? filename.substring(idx).toLowerCase() : '';
}

function isOfficeFile(filename) {
    return OFFICE_EXTENSIONS.includes(getExtension(filename));
}

function isBrowserViewable(filename) {
    return BROWSER_VIEWABLE_EXTENSIONS.includes(getExtension(filename));
}

function isImageFile(filename) {
    const ext = getExtension(filename);
    return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'].includes(ext);
}

function isVideoFile(filename) {
    const ext = getExtension(filename);
    return ['.mp4', '.webm', '.ogg', '.mov'].includes(ext);
}

function isAudioFile(filename) {
    const ext = getExtension(filename);
    return ['.mp3', '.wav', '.ogg', '.flac', '.aac'].includes(ext);
}

function isTextFile(filename) {
    const ext = getExtension(filename);
    return ['.txt', '.csv', '.json', '.xml', '.md', '.log', '.html', '.css', '.js', '.ts', '.py', '.yaml', '.yml', '.toml', '.env', '.sh', '.bat'].includes(ext);
}

function isPdfFile(filename) {
    return getExtension(filename) === '.pdf';
}

/**
 * FilePreviewModal — shows a preview of a file in a dialog.
 * 
 * - PDF → iframe embed
 * - Image → <img> tag
 * - Video → <video> tag
 * - Audio → <audio> tag
 * - Text/Code → fetched content in <pre>
 * - Office files → OnlyOffice editor modal (if enabled) or fallback message
 * - Unknown → file info + download button
 */
export default function FilePreviewModal({ file, open, onClose }) {
    const [textContent, setTextContent] = useState('');
    const [textLoading, setTextLoading] = useState(false);
    const [onlyOfficeEnabled, setOnlyOfficeEnabled] = useState(false);
    const [onlyOfficeFileId, setOnlyOfficeFileId] = useState(null);
    const [onlyOfficeOpen, setOnlyOfficeOpen] = useState(false);

    const fileUrl = file ? `${BACKEND_URL}${file.url}` : '';
    const filename = file?.filename || '';
    const ext = getExtension(filename);

    // Check OnlyOffice availability on mount
    useEffect(() => {
        api.get('/onlyoffice/status')
            .then(res => setOnlyOfficeEnabled(res.data?.enabled === true))
            .catch(() => setOnlyOfficeEnabled(false));
    }, []);

    // Fetch text content
    useEffect(() => {
        if (!open || !file || !isTextFile(filename)) {
            setTextContent('');
            return;
        }
        setTextLoading(true);
        fetch(fileUrl)
            .then(r => r.text())
            .then(t => { setTextContent(t); setTextLoading(false); })
            .catch(() => { setTextContent('Gagal memuat konten file.'); setTextLoading(false); });
    }, [open, file, filename, fileUrl]);

    if (!file) return null;

    const handleOpenOnlyOffice = () => {
        setOnlyOfficeFileId(file.id);
        setOnlyOfficeOpen(true);
    };

    const handleCloseOnlyOffice = () => {
        setOnlyOfficeOpen(false);
        setOnlyOfficeFileId(null);
    };

    const handleOpenInNewTab = (e) => {
        if (e) e.stopPropagation();
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
    };

    const handleDownload = (e) => {
        if (e) e.stopPropagation();
        const a = document.createElement('a');
        a.href = fileUrl;
        a.download = filename;
        a.click();
    };

    // Determine preview type
    let previewContent;

    if (isPdfFile(filename)) {
        previewContent = (
            <iframe
                src={fileUrl}
                title={filename}
                className="w-full h-full border-0"
                style={{ minHeight: '60vh' }}
            />
        );
    } else if (isImageFile(filename)) {
        previewContent = (
            <div className="flex items-center justify-center bg-black/5 p-4" style={{ minHeight: '50vh' }}>
                <img
                    src={fileUrl}
                    alt={filename}
                    className="max-w-full max-h-[70vh] object-contain rounded"
                />
            </div>
        );
    } else if (isVideoFile(filename)) {
        previewContent = (
            <div className="flex items-center justify-center bg-black p-4" style={{ minHeight: '50vh' }}>
                <video
                    src={fileUrl}
                    controls
                    className="max-w-full max-h-[70vh] rounded"
                >
                    Browser tidak mendukung video playback.
                </video>
            </div>
        );
    } else if (isAudioFile(filename)) {
        previewContent = (
            <div className="flex flex-col items-center justify-center p-8" style={{ minHeight: '30vh' }}>
                <FileText className="w-16 h-16 text-brand-primary mb-4" />
                <p className="text-sm text-brand-text-secondary mb-4">{filename}</p>
                <audio src={fileUrl} controls className="w-full max-w-md">
                    Browser tidak mendukung audio playback.
                </audio>
            </div>
        );
    } else if (isTextFile(filename)) {
        previewContent = (
            <div className="bg-gray-900 text-green-400 p-4 rounded overflow-auto font-mono text-sm" style={{ minHeight: '50vh', maxHeight: '70vh' }}>
                {textLoading ? (
                    <div className="flex items-center justify-center h-32">
                        <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                ) : (
                    <pre className="whitespace-pre-wrap break-words">{textContent}</pre>
                )}
            </div>
        );
    } else if (isOfficeFile(filename)) {
        if (onlyOfficeEnabled) {
            previewContent = (
                <div className="flex flex-col items-center justify-center p-8" style={{ minHeight: '40vh' }}>
                    <FileText className="w-16 h-16 text-brand-primary mb-4" />
                    <p className="text-lg font-medium text-brand-text-primary mb-2">{filename}</p>
                    <p className="text-sm text-brand-text-secondary mb-6">
                        File Office — klik tombol di bawah untuk membuka dengan OnlyOffice
                    </p>
                    <Button onClick={handleOpenOnlyOffice} className="bg-brand-primary hover:bg-brand-primary-dark">
                        Buka dengan OnlyOffice
                    </Button>
                </div>
            );
        } else {
            previewContent = (
                <div className="flex flex-col items-center justify-center p-8" style={{ minHeight: '40vh' }}>
                    <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
                    <p className="text-lg font-medium text-brand-text-primary mb-2">{filename}</p>
                    <p className="text-sm text-brand-text-secondary mb-4">
                        Preview tidak tersedia untuk file Office. Silakan download untuk membuka.
                    </p>
                    <Button onClick={handleDownload} variant="outline">
                        <Download className="w-4 h-4 mr-2" /> Download File
                    </Button>
                </div>
            );
        }
    } else {
        // Unknown file type
        previewContent = (
            <div className="flex flex-col items-center justify-center p-8" style={{ minHeight: '40vh' }}>
                <FileText className="w-16 h-16 text-brand-text-muted mb-4" />
                <p className="text-lg font-medium text-brand-text-primary mb-2">{filename}</p>
                <p className="text-sm text-brand-text-secondary mb-1">
                    {file.size ? `${(file.size / 1024).toFixed(1)} KB` : ''}
                </p>
                <p className="text-sm text-brand-text-secondary mb-4">
                    Preview tidak tersedia untuk tipe file ini.
                </p>
                <Button onClick={handleDownload} variant="outline">
                    <Download className="w-4 h-4 mr-2" /> Download File
                </Button>
            </div>
        );
    }

    return (
        <>
            <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
                <DialogContent hideCloseButton className="max-w-[90vw] w-[90vw] sm:max-w-4xl p-0 flex flex-col" data-testid="file-preview-modal">
                    <DialogHeader className="px-4 py-3 border-b flex flex-row items-center justify-between space-y-0">
                        <DialogTitle className="text-base truncate mr-4">{filename}</DialogTitle>
                        <div className="flex items-center gap-1 flex-shrink-0">
                            {isBrowserViewable(filename) && (
                                <Button variant="ghost" size="sm" onClick={handleOpenInNewTab} title="Buka di tab baru">
                                    <ExternalLink className="w-4 h-4" />
                                </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={handleDownload} title="Download">
                                <Download className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={onClose} title="Tutup">
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                    </DialogHeader>
                    <div className="flex-1 overflow-auto">
                        {previewContent}
                    </div>
                </DialogContent>
            </Dialog>

            {/* OnlyOffice Editor Modal for Office files */}
            <OnlyOfficeEditorModal
                fileId={onlyOfficeFileId}
                open={onlyOfficeOpen}
                onClose={handleCloseOnlyOffice}
            />
        </>
    );
}

// Export helper functions for use in FileManagerPage
export { isOfficeFile, isBrowserViewable, getExtension };